import { CLASSES, ENEMIES, RELICS } from "../game/content";
import type { ClassId, EnemyDefinition } from "../game/types";

function hash(input: string): number {
  let result = 7;
  for (const char of input) result = (result * 31 + char.charCodeAt(0)) >>> 0;
  return result;
}

const points = (seed: number, count: number, radius: number, cx = 50, cy = 50) =>
  Array.from({ length: count }, (_, index) => {
    const angle = ((Math.PI * 2) / count) * index + (seed % 19) / 10;
    const wobble = radius * (0.72 + (((seed >> (index % 12)) & 7) / 24));
    return `${cx + Math.cos(angle) * wobble},${cy + Math.sin(angle) * wobble}`;
  }).join(" ");

export function CardArt({ art, palette, label }: { art: string; palette: [string, string, string]; label: string }) {
  const seed = hash(art);
  const a = 8 + (seed % 16);
  const b = 62 + ((seed >> 5) % 24);
  const c = 22 + ((seed >> 10) % 42);
  const rays = 5 + (seed % 6);
  return (
    <svg className="card-art-svg" viewBox="0 0 100 76" role="img" aria-label={`${label} illustration`}>
      <defs>
        <linearGradient id={`g-${seed}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={palette[2]} />
          <stop offset="0.55" stopColor={palette[0]} />
          <stop offset="1" stopColor={palette[1]} />
        </linearGradient>
        <radialGradient id={`r-${seed}`} cx="50%" cy="45%" r="65%">
          <stop offset="0" stopColor={palette[1]} stopOpacity=".88" />
          <stop offset=".42" stopColor={palette[0]} stopOpacity=".35" />
          <stop offset="1" stopColor={palette[2]} stopOpacity="0" />
        </radialGradient>
        <filter id={`blur-${seed}`}><feGaussianBlur stdDeviation="2.4" /></filter>
      </defs>
      <rect width="100" height="76" rx="5" fill={`url(#g-${seed})`} />
      <circle cx={c} cy={a + 13} r={13 + (seed % 8)} fill={`url(#r-${seed})`} filter={`url(#blur-${seed})`} />
      {Array.from({ length: rays }, (_, index) => {
        const x1 = (seed + index * 17) % 100;
        const x2 = (seed * 3 + index * 29) % 100;
        return <path key={index} d={`M ${x1} 76 Q 50 ${b - index * 3} ${x2} 0`} stroke={index % 2 ? palette[1] : "white"} strokeOpacity={0.08 + index * 0.018} fill="none" strokeWidth={1 + (index % 3)} />;
      })}
      <polygon points={points(seed, 5 + (seed % 4), 22 + (seed % 9), 50, 40)} fill={palette[2]} fillOpacity=".72" stroke={palette[1]} strokeWidth="1.2" />
      <polygon points={points(seed >> 2, 3 + (seed % 5), 12 + (seed % 7), 50, 40)} fill={palette[0]} fillOpacity=".7" stroke="white" strokeOpacity=".45" />
      <circle cx="50" cy="40" r={4 + (seed % 5)} fill={palette[1]} stroke="white" strokeOpacity=".75" />
      <path d={`M ${a} 63 C ${c} 44, ${b} 54, ${92 - a} 15`} fill="none" stroke="white" strokeOpacity=".28" strokeWidth="1" strokeDasharray={`${2 + seed % 4} 3`} />
      <rect x="2" y="2" width="96" height="72" rx="4" fill="none" stroke="white" strokeOpacity=".24" />
    </svg>
  );
}

export function CharacterPortrait({ classId, compact = false }: { classId: ClassId; compact?: boolean }) {
  const cls = CLASSES[classId];
  const [primary, accent, dark] = cls.palette;
  const common = (
    <>
      <defs>
        <radialGradient id={`char-bg-${classId}`} cx="50%" cy="35%" r="75%">
          <stop offset="0" stopColor={primary} stopOpacity=".45" />
          <stop offset=".55" stopColor={dark} />
          <stop offset="1" stopColor="#05070c" />
        </radialGradient>
        <filter id={`char-glow-${classId}`}><feGaussianBlur stdDeviation="3" /></filter>
      </defs>
      <rect width="240" height="300" fill={`url(#char-bg-${classId})`} />
      <circle cx="120" cy="88" r="62" fill={accent} opacity=".12" filter={`url(#char-glow-${classId})`} />
    </>
  );
  return (
    <svg className={`character-art ${compact ? "compact" : ""}`} viewBox="0 0 240 300" role="img" aria-label={`${cls.character}, ${cls.name}`}>
      {common}
      {classId === "ironbound" && <>
        <path d="M48 280 L62 142 L86 112 L95 59 L145 52 L159 111 L190 142 L210 280 Z" fill="#1b2931" stroke={accent} strokeWidth="3" />
        <path d="M81 111 L45 139 L31 214 L63 222 L87 159 Z" fill="#293b44" stroke={primary} strokeWidth="4" />
        <path d="M158 112 L199 135 L218 207 L186 218 L154 158 Z" fill="#293b44" stroke={primary} strokeWidth="4" />
        <path d="M91 62 Q120 36 149 61 L145 108 Q120 127 95 106 Z" fill="#9b6753" />
        <path d="M89 66 Q119 37 153 58 L147 79 L91 84 Z" fill="#202a30" stroke={accent} strokeWidth="2" />
        <rect x="102" y="78" width="48" height="9" rx="4" fill={accent} opacity=".85" />
        <circle cx="120" cy="164" r="33" fill={primary} opacity=".24" filter={`url(#char-glow-${classId})`} />
        <circle cx="120" cy="164" r="18" fill="#16232b" stroke={primary} strokeWidth="5" />
        <path d="M111 164 L120 148 L129 164 L120 181 Z" fill={accent} />
        <path d="M66 169 L84 173 M177 169 L158 173 M108 198 L103 267 M132 198 L139 267" stroke={accent} strokeWidth="3" opacity=".55" />
      </>}
      {classId === "riftweaver" && <>
        <path d="M55 286 L73 130 L94 108 L90 64 Q120 36 150 62 L145 108 L170 132 L194 286 Z" fill="#201b35" stroke={primary} strokeWidth="3" />
        <path d="M94 62 Q119 38 150 61 L145 107 Q119 122 94 106 Z" fill="#8a6258" />
        <path d="M84 70 Q119 38 159 64 L142 87 L88 88 Z" fill="#191426" />
        <path d="M72 87 C103 69 137 70 171 90 C148 106 101 109 72 87 Z" fill={primary} opacity=".48" stroke={accent} strokeWidth="1.5" />
        <circle cx="108" cy="88" r="4" fill={accent} /><circle cx="139" cy="87" r="4" fill={accent} />
        {Array.from({ length: 7 }, (_, i) => <polygon key={i} points={`${35 + i * 28},${75 + (i % 2) * 35} ${47 + i * 24},${50 + (i % 3) * 22} ${55 + i * 22},${92 + (i % 2) * 28}`} fill="none" stroke={i % 2 ? accent : primary} opacity=".52" />)}
        <path d="M89 134 Q120 166 151 134 L165 272 L76 272 Z" fill="#2f2350" />
        <path d="M80 154 C102 133 142 134 164 157 M77 189 C107 170 139 172 169 192 M73 226 C105 204 143 208 173 229" fill="none" stroke={accent} opacity=".4" />
        <circle cx="120" cy="164" r="23" fill={accent} opacity=".15" filter={`url(#char-glow-${classId})`} />
      </>}
      {classId === "wayfinder" && <>
        <path d="M49 286 L68 138 L91 111 L93 64 Q120 40 148 62 L151 110 L177 138 L196 286 Z" fill="#202c2d" stroke={primary} strokeWidth="3" />
        <path d="M93 65 Q120 42 149 63 L145 106 Q120 123 96 105 Z" fill="#936557" />
        <path d="M88 69 Q120 35 157 64 L151 78 L90 88 Z" fill="#272a27" />
        <path d="M88 86 L153 77 L148 94 L91 99 Z" fill={primary} opacity=".55" />
        <circle cx="139" cy="87" r="7" fill="#151b1b" stroke={primary} strokeWidth="3" /><circle cx="139" cy="87" r="2" fill={accent} />
        <path d="M68 137 L36 194 L57 214 L92 158 Z" fill="#263637" stroke={accent} strokeWidth="2" />
        <path d="M160 128 L207 50 L221 58 L176 148" fill="#26363a" stroke={primary} strokeWidth="4" />
        <rect x="174" y="78" width="16" height="112" rx="5" transform="rotate(28 182 134)" fill="#172126" stroke={accent} strokeWidth="2" />
        <path d="M82 126 Q120 146 174 127 L188 281 L58 281 Z" fill="#344444" />
        <path d="M64 146 Q108 167 184 142" fill="none" stroke={accent} strokeWidth="3" opacity=".55" />
        <circle cx="53" cy="98" r="14" fill="#1c2829" stroke={accent} strokeWidth="2" /><path d="M45 98 H61 M53 90 V106" stroke={primary} />
      </>}
      {classId === "concord" && <>
        <path d="M48 286 L67 136 L91 109 L94 63 Q120 40 147 62 L150 109 L178 136 L196 286 Z" fill="#202c43" stroke={primary} strokeWidth="3" />
        <path d="M94 64 Q120 41 148 63 L144 106 Q119 122 96 105 Z" fill="#825f58" />
        <path d="M92 67 Q120 37 154 63 L147 81 L91 84 Z" fill="#19233a" />
        <path d="M101 88 Q120 96 140 87" fill="none" stroke={accent} strokeWidth="2" />
        <path d="M76 126 Q120 150 170 125 L184 279 L57 279 Z" fill="#263754" />
        <path d="M102 136 L120 153 L138 136 L133 193 L120 205 L107 193 Z" fill={accent} opacity=".75" />
        <path d="M113 156 L120 162 L127 156 L125 183 L120 188 L115 183 Z" fill={primary} />
        {[[32,113],[48,62],[192,62],[208,113]].map(([x,y], i) => <g key={i} opacity=".55"><circle cx={x} cy={y} r="19" fill={primary} opacity=".18" /><path d={`M${x-8} ${y+12} Q${x} ${y-17} ${x+8} ${y+12}`} fill="none" stroke={accent} strokeWidth="2" /><circle cx={x} cy={y-7} r="5" fill={accent} /></g>)}
        <ellipse cx="120" cy="206" rx="74" ry="22" fill="none" stroke={primary} strokeWidth="3" opacity=".42" />
      </>}
      <path d="M0 265 Q60 240 120 270 T240 260 V300 H0Z" fill="#05070b" opacity=".7" />
    </svg>
  );
}

function enemyGeometry(def: EnemyDefinition) {
  const seed = hash(def.art);
  const [primary, accent, dark] = def.palette;
  const boss = def.tier === "boss";
  const elite = def.tier === "elite";
  return <>
    <defs>
      <radialGradient id={`enemy-${def.id}`} cx="50%" cy="38%" r="66%">
        <stop offset="0" stopColor={primary} stopOpacity=".62" />
        <stop offset=".52" stopColor={dark} />
        <stop offset="1" stopColor="#04060a" />
      </radialGradient>
      <filter id={`enemy-glow-${def.id}`}><feGaussianBlur stdDeviation={boss ? 5 : 3} /></filter>
    </defs>
    <rect width="300" height="220" rx="18" fill={`url(#enemy-${def.id})`} />
    <circle cx="150" cy="98" r={boss ? 78 : elite ? 62 : 50} fill={accent} opacity=".13" filter={`url(#enemy-glow-${def.id})`} />
    {boss && Array.from({ length: 10 }, (_, i) => <path key={i} d={`M150 108 L${20 + i * 29} ${15 + (i % 3) * 18}`} stroke={i % 2 ? accent : primary} strokeWidth="4" opacity=".28" />)}
    <polygon points={points(seed, boss ? 10 : elite ? 8 : 6, boss ? 74 : elite ? 57 : 45, 150, 105)} fill={dark} stroke={primary} strokeWidth={boss ? 6 : 4} />
    <polygon points={points(seed >> 3, 5 + seed % 4, boss ? 49 : 34, 150, 101)} fill={primary} opacity=".35" stroke={accent} strokeWidth="3" />
    {Array.from({ length: boss ? 5 : elite ? 4 : 3 }, (_, i) => {
      const x = 150 + Math.cos((i / (boss ? 5 : elite ? 4 : 3)) * Math.PI * 2 + seed) * (boss ? 39 : 28);
      const y = 101 + Math.sin((i / (boss ? 5 : elite ? 4 : 3)) * Math.PI * 2 + seed) * (boss ? 31 : 22);
      return <circle key={i} cx={x} cy={y} r={boss ? 7 : 5} fill={accent} stroke="white" strokeOpacity=".5" />;
    })}
    <path d={`M${45 + seed % 35} 190 Q150 ${130 + seed % 30} ${255 - seed % 35} 190`} fill="none" stroke={accent} strokeWidth={boss ? 8 : 5} opacity=".45" />
    <path d="M0 190 Q80 168 150 192 T300 184 V220 H0Z" fill="#04060a" opacity=".78" />
  </>;
}

export function EnemyPortrait({ enemyId }: { enemyId: string }) {
  const def = ENEMIES[enemyId];
  return <svg className={`enemy-art ${def.tier}`} viewBox="0 0 300 220" role="img" aria-label={`${def.name}, ${def.subtitle}`}>{enemyGeometry(def)}</svg>;
}

export function RelicIcon({ relicId, size = 52 }: { relicId: string; size?: number }) {
  const relic = RELICS[relicId];
  const seed = hash(relic.art);
  return (
    <svg className="relic-icon" width={size} height={size} viewBox="0 0 64 64" role="img" aria-label={relic.name}>
      <defs><radialGradient id={`relic-${seed}`}><stop offset="0" stopColor="#ffe49a" /><stop offset=".5" stopColor="#bd6e46" /><stop offset="1" stopColor="#17131d" /></radialGradient></defs>
      <circle cx="32" cy="32" r="29" fill="#0c1019" stroke="#e2b86d" strokeWidth="2" />
      <polygon points={points(seed, 5 + seed % 5, 22, 32, 32)} fill={`url(#relic-${seed})`} stroke="#ffeab0" strokeWidth="1.5" />
      <circle cx="32" cy="32" r={5 + seed % 8} fill="#72d9d7" opacity=".8" />
    </svg>
  );
}
